import { useNavigate } from 'react-router-dom';
import { ChevronRight, PhoneCall, PhoneForwarded, PhoneMissed, Undo2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTransferNotices } from '../contexts/TransferNoticesContext';
import {
  formatPhoneDisplay,
  transferStatusLabel,
  type TransferNotice,
  type TransferStatus,
} from '@/lib/transferBanner';

const STATUS_ICONS: Record<TransferStatus, typeof PhoneForwarded> = {
  requested: PhoneForwarded,
  answered: PhoneCall,
  no_answer: PhoneMissed,
  comeback: Undo2,
};

/**
 * Live phone transfers (`call.transfer`) as slim rows under the TopBar:
 * "Anruf von +49… – Weiterleitung an Bonn: klingelt …". One row per call,
 * newest first, at most three; finished ones disappear after 60 s (state in
 * TransferNoticesProvider). A click opens the call in Verläufe via the same
 * `phone` / `type` / `at` params the dashboard jump uses.
 *
 * The polite live region is rendered even while empty: screen readers only
 * announce changes inside a region that already existed, so one that mounts
 * together with its first notice would stay silent.
 */
export function TransferBanner() {
  const navigate = useNavigate();
  const visible = useTransferNotices();
  const hasNotices = visible.length > 0;

  const openConversation = (notice: TransferNotice) => {
    const params = new URLSearchParams({
      phone: notice.customerPhone.replace(/\D/g, ''),
      type: 'phone',
    });
    if (notice.at) params.set('at', notice.at);
    navigate(`/conversations?${params.toString()}`);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      // Announce the row that changed, not all (up to three) rows again.
      aria-atomic="false"
      className={cn(hasNotices && 'border-t border-topbar-foreground/15')}
    >
      {hasNotices && (
        <ul className="flex flex-col divide-y divide-topbar-foreground/10 px-4">
          {visible.map((notice) => {
            const Icon = STATUS_ICONS[notice.status] ?? PhoneForwarded;
            const ringing = notice.status === 'requested';
            return (
              <li key={notice.callSid}>
                <button
                  type="button"
                  onClick={() => openConversation(notice)}
                  className="group -mx-2 flex w-[calc(100%+1rem)] items-center gap-2.5 rounded px-2 py-1.5 text-left text-xs text-topbar-foreground transition-colors hover:bg-topbar-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-topbar-foreground/60"
                >
                  <span className="relative flex h-2 w-2 flex-shrink-0" aria-hidden="true">
                    {ringing && (
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75 motion-reduce:animate-none" />
                    )}
                    <span
                      className={cn(
                        'relative inline-flex h-2 w-2 rounded-full',
                        ringing ? 'bg-success' : 'bg-topbar-foreground/45',
                      )}
                    />
                  </span>
                  <Icon className="h-3.5 w-3.5 flex-shrink-0 opacity-90" aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate">
                    <span className="opacity-80">Anruf von </span>
                    <span className="font-semibold tabular-nums">{formatPhoneDisplay(notice.customerPhone)}</span>
                    <span className="opacity-80"> – Weiterleitung an </span>
                    <span className="font-semibold">{notice.targetLabel}</span>
                    {notice.attempt > 1 && <span className="opacity-70"> (Versuch {notice.attempt})</span>}
                    <span className="opacity-80">: </span>
                    <span className={cn(ringing ? 'font-semibold' : 'opacity-90')}>
                      {transferStatusLabel(notice.status)}
                    </span>
                  </span>
                  <span className="hidden flex-shrink-0 items-center gap-0.5 text-[11px] opacity-0 transition-opacity group-hover:opacity-80 group-focus-visible:opacity-80 sm:inline-flex">
                    Verlauf öffnen
                    <ChevronRight className="h-3 w-3" aria-hidden="true" />
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default TransferBanner;
